import React from 'react';
import { X, Users, UserPlus, Search, Image, Bell, Clock, LogOut, Shield, Trash2, ChevronRight } from 'lucide-react';

const GroupInfoSidebar = ({
  chat, groupMembers, isCurrentUserAdmin, isDarkMode, user, token,
  groupMediaLoaded, groupSharedMedia,
  onClose, onAddMember, onMakeAdmin, onRemoveAdmin, onRemoveMember,
  onLeaveGroup, onDeleteGroup, onSearchChat, onViewContactProfile,
  onSetDisappearing, onMuteToggle, onMediaClick,
  API, axios
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
      <div className={`w-full max-w-md h-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'} overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 glass-effect border-b border-border-DEFAULT p-4 flex items-center gap-3 z-10">
          <button onClick={onClose} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
            <X size={20} />
          </button>
          <h2 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Group Info</h2>
        </div>

        {/* Group Name & Actions */}
        <div className={`p-6 text-center border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center mx-auto mb-4">
            <Users size={36} className="text-white" />
          </div>
          <h3 className={`text-xl font-heading ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{chat?.group_name || 'Group Chat'}</h3>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>Group · {groupMembers.length} members</p>

          <div className="flex items-center justify-center gap-3 mt-5">
            {isCurrentUserAdmin && (
              <button onClick={onAddMember} className={`flex flex-col items-center gap-2 px-6 py-3 rounded-2xl border transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`} data-testid="add-member-btn">
                <UserPlus size={22} className="text-[#D4AF37]" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Add</span>
              </button>
            )}
            <button onClick={onSearchChat} className={`flex flex-col items-center gap-2 px-6 py-3 rounded-2xl border transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}>
              <Search size={22} className="text-[#D4AF37]" />
              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Search</span>
            </button>
          </div>
        </div>

        {/* Media */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Image size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Media, links and docs</span>
            </div>
            <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {(groupSharedMedia.images?.length || 0) + (groupSharedMedia.videos?.length || 0) + (groupSharedMedia.documents?.length || 0) + (groupSharedMedia.links?.length || 0)}
            </span>
          </div>
          {groupMediaLoaded && (
            <div className="flex gap-1 overflow-x-auto pb-2">
              {[...(groupSharedMedia.images || []), ...(groupSharedMedia.videos || [])].slice(0, 5).map((item, i) => (
                <div key={i} className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer hover:opacity-80" onClick={() => onMediaClick({ url: item.url, type: item.type === 'video' ? 'video' : 'image' })}>
                  {item.type === 'video' ? <video src={item.url} className="w-full h-full object-cover" /> : <img src={item.url} alt="" className="w-full h-full object-cover" />}
                </div>
              ))}
              {(groupSharedMedia.images?.length || 0) + (groupSharedMedia.videos?.length || 0) === 0 && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No media yet</p>
              )}
            </div>
          )}
          {!groupMediaLoaded && <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading...</div>}
        </div>

        {/* Mute & Disappearing */}
        <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
          <button onClick={onMuteToggle} className={`w-full flex items-center gap-4 p-4 transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
            <Bell size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Mute notifications</span>
          </button>
        </div>
        <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
          <div className="p-4">
            <div className="flex items-center gap-4 mb-3">
              <Clock size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Disappearing messages</span>
            </div>
            <div className="flex gap-2 ml-9">
              {[{ label: 'Off', value: 0 }, { label: '24h', value: 86400 }, { label: '7d', value: 604800 }, { label: '90d', value: 7776000 }].map(opt => (
                <button key={opt.value} onClick={() => onSetDisappearing(opt.value, opt.label)} className={`px-3 py-1.5 rounded-full text-xs transition-colors ${(chat?.disappearing_messages || 0) === opt.value ? 'bg-[#D4AF37] text-white' : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Members */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
          <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>{groupMembers.length} MEMBERS</h4>
          <div className="space-y-1">
            {groupMembers.map(member => (
              <div key={member.id} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                onClick={() => member.id !== user.id && onViewContactProfile(member.id)}>
                <div className="relative">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                    {(member.display_name || '?').charAt(0).toUpperCase()}
                  </div>
                  {member.is_online && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{member.display_name}{member.id === user.id && ' (You)'}</span>
                    {member.is_admin && <span className="px-2 py-0.5 rounded-full text-xs bg-[#D4AF37]/20 text-[#D4AF37]">{member.is_creator ? 'Creator' : 'Admin'}</span>}
                  </div>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{member.username}</span>
                </div>
                {isCurrentUserAdmin && member.id !== user.id && (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {!member.is_admin ? (
                      <button onClick={() => onMakeAdmin(member.id)} className={`px-2 py-1 text-xs rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Admin</button>
                    ) : !member.is_creator && (
                      <button onClick={() => onRemoveAdmin(member.id)} className={`px-2 py-1 text-xs rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Demote</button>
                    )}
                    {!member.is_creator && (
                      <button onClick={() => onRemoveMember(member.id)} className="px-2 py-1 text-xs rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20">Remove</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 space-y-1">
          {chat?.created_by !== user.id && (
            <button onClick={onLeaveGroup} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`} data-testid="exit-group-btn">
              <LogOut size={20} className="text-red-500" /><span className="text-red-500">Exit Group</span>
            </button>
          )}
          <button onClick={() => {}} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`} data-testid="report-group-btn">
            <Shield size={20} className="text-red-500" /><span className="text-red-500">Report Group</span>
          </button>
          {chat?.created_by === user.id && (
            <button onClick={onDeleteGroup} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`} data-testid="delete-group-btn">
              <Trash2 size={20} className="text-red-500" /><span className="text-red-500">Delete Group</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupInfoSidebar;
